/**
 * Do not edit manually.
 * Event Management Service API
 * API for managing events, invitations, and guests
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ArrayOfCity,
  ArrayOfCostCentre,
  ArrayOfGuest,
  ArrayOfInvitation,
  ArrayOfPolicy,
  ArrayOfSupportedTravelModeCombination,
  BadRequestResponse,
  BookingConfirmedRequestBody,
  BookingDetails,
  CitySearchRequestBody,
  Country,
  CreateGuestRequestBody,
  CreateIbiPayloadResponse,
  CreateInvitationRequestBody,
  CurrentUserProfile,
  DuplicateInvitationRequestBody,
  Error,
  GetCostCentresParams,
  GetCountryCodeParams,
  Guest,
  GuestAuthRequestBody,
  GuestAuthResponse,
  Invitation,
  IsEnableGenderNeutralInboundSupportParams,
  ListGuestsRequestBody,
  LoggingLevelResponse,
  LookupTravellerByEmailParams,
  NotFoundResponse,
  PolicySearchRequestBody,
  ResendCodeRequestBody,
  SearchGuestsParams,
  SearchInvitationRequestBody,
  SetLogLevelRequest,
  SetLogLevelResponse,
  SupportedTravelModeCombinationsRequestBody,
  TravellerProfileLookup,
  UnauthorizedResponse,
  UpdateInvitationRequestBody,
  UpdateInvitationStatusRequestBody,
  UpdateProfileRequestBody,
  VerifyCodeRequestBody,
  VerifyInvitedGuestTokenParams,
} from './models';

import createInvitationMutator from '../mutator';
import searchInvitationsMutator from '../mutator';
import searchCitiesMutator from '../mutator';
import searchPolicyMutator from '../mutator';
import getSupportedTravelModeCombinationsMutator from '../mutator';
import getCostCentresMutator from '../mutator';
import getBookingDetailsMutator from '../mutator';
import getInvitationByIdMutator from '../mutator';
import updateInvitationMutator from '../mutator';
import updateInvitationStatusMutator from '../mutator';
import cancelInvitationMutator from '../mutator';
import resendInvitationMutator from '../mutator';
import duplicateInvitationMutator from '../mutator';
import getOneTimeTokenMutator from '../mutator';
import verifyInvitedGuestTokenMutator from '../mutator';
import listAllGuestsMutator from '../mutator';
import getTitlesMutator from '../mutator';
import getCountriesMutator from '../mutator';
import isEnableGenderNeutralInboundSupportMutator from '../mutator';
import getCountryCodeMutator from '../mutator';
import getGuestByIdMutator from '../mutator';
import updateGuestProfileMutator from '../mutator';
import createGuestProfileMutator from '../mutator';
import getGuestInvitationByIdMutator from '../mutator';
import updateGuestInvitationMutator from '../mutator';
import confirmGuestBookingMutator from '../mutator';
import getVerifyCodeMutator from '../mutator';
import createGuestIbiPayloadMutator from '../mutator';
import lookupTravellerByEmailMutator from '../mutator';
import searchGuestsMutator from '../mutator';
import getLoggingLevelMutator from '../mutator';
import setLoggingLevelMutator from '../mutator';
import guestAuthByTokenMutator from '../mutator';
import resendGuestAuthCodeMutator from '../mutator';
import verifyGuestAuthCodeMutator from '../mutator';
import getUserProfileMutator from '../mutator';
type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * @summary Create invitation
 */
export const createInvitation = (
  createInvitationRequestBody: CreateInvitationRequestBody,
  options?: SecondParameter<typeof createInvitationMutator>,
  signal?: AbortSignal
) => {
  return createInvitationMutator<Invitation>(
    {
      url: `/v1/invitations`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createInvitationRequestBody,
      signal,
    },
    options
  );
};

export const getCreateInvitationMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createInvitation>>,
    TError,
    { data: CreateInvitationRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof createInvitationMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createInvitation>>,
  TError,
  { data: CreateInvitationRequestBody },
  TContext
> => {
  const mutationKey = ['createInvitation'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createInvitation>>,
    { data: CreateInvitationRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return createInvitation(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateInvitationMutationResult = NonNullable<
  Awaited<ReturnType<typeof createInvitation>>
>;
export type CreateInvitationMutationBody = CreateInvitationRequestBody;
export type CreateInvitationMutationError = Error;

/**
 * @summary Create invitation
 */
export const useCreateInvitation = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createInvitation>>,
      TError,
      { data: CreateInvitationRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof createInvitationMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createInvitation>>,
  TError,
  { data: CreateInvitationRequestBody },
  TContext
> => {
  return useMutation(getCreateInvitationMutationOptions(options), queryClient);
};

/**
 * @summary Search invitations (filters, paging, search)
 */
export const searchInvitations = (
  searchInvitationRequestBody: SearchInvitationRequestBody,
  options?: SecondParameter<typeof searchInvitationsMutator>,
  signal?: AbortSignal
) => {
  return searchInvitationsMutator<ArrayOfInvitation>(
    {
      url: `/v1/invitations/search`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: searchInvitationRequestBody,
      signal,
    },
    options
  );
};

export const getSearchInvitationsMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchInvitations>>,
    TError,
    { data: SearchInvitationRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof searchInvitationsMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchInvitations>>,
  TError,
  { data: SearchInvitationRequestBody },
  TContext
> => {
  const mutationKey = ['searchInvitations'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchInvitations>>,
    { data: SearchInvitationRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return searchInvitations(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchInvitationsMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchInvitations>>
>;
export type SearchInvitationsMutationBody = SearchInvitationRequestBody;
export type SearchInvitationsMutationError = Error;

/**
 * @summary Search invitations (filters, paging, search)
 */
export const useSearchInvitations = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchInvitations>>,
      TError,
      { data: SearchInvitationRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof searchInvitationsMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof searchInvitations>>,
  TError,
  { data: SearchInvitationRequestBody },
  TContext
> => {
  return useMutation(getSearchInvitationsMutationOptions(options), queryClient);
};

/**
 * @summary Search cities for invitation
 */
export const searchCities = (
  citySearchRequestBody: CitySearchRequestBody,
  options?: SecondParameter<typeof searchCitiesMutator>,
  signal?: AbortSignal
) => {
  return searchCitiesMutator<ArrayOfCity>(
    {
      url: `/v1/invitations/citysearch`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: citySearchRequestBody,
      signal,
    },
    options
  );
};

export const getSearchCitiesMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchCities>>,
    TError,
    { data: CitySearchRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof searchCitiesMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchCities>>,
  TError,
  { data: CitySearchRequestBody },
  TContext
> => {
  const mutationKey = ['searchCities'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchCities>>,
    { data: CitySearchRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return searchCities(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchCitiesMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchCities>>
>;
export type SearchCitiesMutationBody = CitySearchRequestBody;
export type SearchCitiesMutationError = Error;

/**
 * @summary Search cities for invitation
 */
export const useSearchCities = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchCities>>,
      TError,
      { data: CitySearchRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof searchCitiesMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof searchCities>>,
  TError,
  { data: CitySearchRequestBody },
  TContext
> => {
  return useMutation(getSearchCitiesMutationOptions(options), queryClient);
};

/**
 * @summary Search travel policy for invitation
 */
export const searchPolicy = (
  policySearchRequestBody: PolicySearchRequestBody,
  options?: SecondParameter<typeof searchPolicyMutator>,
  signal?: AbortSignal
) => {
  return searchPolicyMutator<ArrayOfPolicy>(
    {
      url: `/v1/invitations/policy`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: policySearchRequestBody,
      signal,
    },
    options
  );
};

export const getSearchPolicyMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchPolicy>>,
    TError,
    { data: PolicySearchRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof searchPolicyMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchPolicy>>,
  TError,
  { data: PolicySearchRequestBody },
  TContext
> => {
  const mutationKey = ['searchPolicy'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchPolicy>>,
    { data: PolicySearchRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return searchPolicy(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchPolicyMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchPolicy>>
>;
export type SearchPolicyMutationBody = PolicySearchRequestBody;
export type SearchPolicyMutationError = Error;

/**
 * @summary Search travel policy for invitation
 */
export const useSearchPolicy = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchPolicy>>,
      TError,
      { data: PolicySearchRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof searchPolicyMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof searchPolicy>>,
  TError,
  { data: PolicySearchRequestBody },
  TContext
> => {
  return useMutation(getSearchPolicyMutationOptions(options), queryClient);
};

/**
 * @summary Get supported travel mode combinations for invitation
 */
export const getSupportedTravelModeCombinations = (
  supportedTravelModeCombinationsRequestBody: SupportedTravelModeCombinationsRequestBody,
  options?: SecondParameter<typeof getSupportedTravelModeCombinationsMutator>,
  signal?: AbortSignal
) => {
  return getSupportedTravelModeCombinationsMutator<ArrayOfSupportedTravelModeCombination>(
    {
      url: `/v1/invitations/supportedTravelModeCombinations`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: supportedTravelModeCombinationsRequestBody,
      signal,
    },
    options
  );
};

export const getGetSupportedTravelModeCombinationsMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getSupportedTravelModeCombinations>>,
    TError,
    { data: SupportedTravelModeCombinationsRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof getSupportedTravelModeCombinationsMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof getSupportedTravelModeCombinations>>,
  TError,
  { data: SupportedTravelModeCombinationsRequestBody },
  TContext
> => {
  const mutationKey = ['getSupportedTravelModeCombinations'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof getSupportedTravelModeCombinations>>,
    { data: SupportedTravelModeCombinationsRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return getSupportedTravelModeCombinations(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GetSupportedTravelModeCombinationsMutationResult = NonNullable<
  Awaited<ReturnType<typeof getSupportedTravelModeCombinations>>
>;
export type GetSupportedTravelModeCombinationsMutationBody =
  SupportedTravelModeCombinationsRequestBody;
export type GetSupportedTravelModeCombinationsMutationError = Error;

/**
 * @summary Get supported travel mode combinations for invitation
 */
export const useGetSupportedTravelModeCombinations = <
  TError = Error,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof getSupportedTravelModeCombinations>>,
      TError,
      { data: SupportedTravelModeCombinationsRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof getSupportedTravelModeCombinationsMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof getSupportedTravelModeCombinations>>,
  TError,
  { data: SupportedTravelModeCombinationsRequestBody },
  TContext
> => {
  return useMutation(
    getGetSupportedTravelModeCombinationsMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Search cost centres (debtors) for invitation
 */
export const getCostCentres = (
  params?: GetCostCentresParams,
  options?: SecondParameter<typeof getCostCentresMutator>,
  signal?: AbortSignal
) => {
  return getCostCentresMutator<ArrayOfCostCentre>(
    { url: `/v1/invitations/costCentres`, method: 'GET', params, signal },
    options
  );
};

export const getGetCostCentresQueryKey = (params?: GetCostCentresParams) => {
  return [`/v1/invitations/costCentres`, ...(params ? [params] : [])] as const;
};

export const getGetCostCentresQueryOptions = <
  TData = Awaited<ReturnType<typeof getCostCentres>>,
  TError = Error,
>(
  params?: GetCostCentresParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCostCentres>>, TError, TData>
    >;
    request?: SecondParameter<typeof getCostCentresMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCostCentresQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCostCentres>>> = ({
    signal,
  }) => getCostCentres(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCostCentres>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCostCentresQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCostCentres>>
>;
export type GetCostCentresQueryError = Error;

export function useGetCostCentres<
  TData = Awaited<ReturnType<typeof getCostCentres>>,
  TError = Error,
>(
  params: undefined | GetCostCentresParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCostCentres>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCostCentres>>,
          TError,
          Awaited<ReturnType<typeof getCostCentres>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getCostCentresMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCostCentres<
  TData = Awaited<ReturnType<typeof getCostCentres>>,
  TError = Error,
>(
  params?: GetCostCentresParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCostCentres>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCostCentres>>,
          TError,
          Awaited<ReturnType<typeof getCostCentres>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getCostCentresMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCostCentres<
  TData = Awaited<ReturnType<typeof getCostCentres>>,
  TError = Error,
>(
  params?: GetCostCentresParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCostCentres>>, TError, TData>
    >;
    request?: SecondParameter<typeof getCostCentresMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Search cost centres (debtors) for invitation
 */

export function useGetCostCentres<
  TData = Awaited<ReturnType<typeof getCostCentres>>,
  TError = Error,
>(
  params?: GetCostCentresParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCostCentres>>, TError, TData>
    >;
    request?: SecondParameter<typeof getCostCentresMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetCostCentresQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get booking details by ID
 */
export const getBookingDetails = (
  id: number,
  invitationId: number,
  options?: SecondParameter<typeof getBookingDetailsMutator>,
  signal?: AbortSignal
) => {
  return getBookingDetailsMutator<BookingDetails>(
    {
      url: `/v1/guests/bookings/${id}/invitation/${invitationId}`,
      method: 'GET',
      signal,
    },
    options
  );
};

export const getGetBookingDetailsQueryKey = (
  id: number,
  invitationId: number
) => {
  return [`/v1/guests/bookings/${id}/invitation/${invitationId}`] as const;
};

export const getGetBookingDetailsQueryOptions = <
  TData = Awaited<ReturnType<typeof getBookingDetails>>,
  TError = Error,
>(
  id: number,
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBookingDetails>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getBookingDetailsMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBookingDetailsQueryKey(id, invitationId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBookingDetails>>
  > = ({ signal }) =>
    getBookingDetails(id, invitationId, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(id && invitationId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getBookingDetails>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetBookingDetailsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBookingDetails>>
>;
export type GetBookingDetailsQueryError = Error;

export function useGetBookingDetails<
  TData = Awaited<ReturnType<typeof getBookingDetails>>,
  TError = Error,
>(
  id: number,
  invitationId: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBookingDetails>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBookingDetails>>,
          TError,
          Awaited<ReturnType<typeof getBookingDetails>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getBookingDetailsMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetBookingDetails<
  TData = Awaited<ReturnType<typeof getBookingDetails>>,
  TError = Error,
>(
  id: number,
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBookingDetails>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBookingDetails>>,
          TError,
          Awaited<ReturnType<typeof getBookingDetails>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getBookingDetailsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetBookingDetails<
  TData = Awaited<ReturnType<typeof getBookingDetails>>,
  TError = Error,
>(
  id: number,
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBookingDetails>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getBookingDetailsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get booking details by ID
 */

export function useGetBookingDetails<
  TData = Awaited<ReturnType<typeof getBookingDetails>>,
  TError = Error,
>(
  id: number,
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBookingDetails>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getBookingDetailsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetBookingDetailsQueryOptions(
    id,
    invitationId,
    options
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get Invitation by Id
 */
export const getInvitationById = (
  id: number,
  options?: SecondParameter<typeof getInvitationByIdMutator>,
  signal?: AbortSignal
) => {
  return getInvitationByIdMutator<Invitation>(
    { url: `/v1/invitations/${id}`, method: 'GET', signal },
    options
  );
};

export const getGetInvitationByIdQueryKey = (id: number) => {
  return [`/v1/invitations/${id}`] as const;
};

export const getGetInvitationByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getInvitationById>>,
  TError = Error,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInvitationById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getInvitationByIdMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvitationByIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getInvitationById>>
  > = ({ signal }) => getInvitationById(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getInvitationById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetInvitationByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getInvitationById>>
>;
export type GetInvitationByIdQueryError = Error;

export function useGetInvitationById<
  TData = Awaited<ReturnType<typeof getInvitationById>>,
  TError = Error,
>(
  id: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInvitationById>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInvitationById>>,
          TError,
          Awaited<ReturnType<typeof getInvitationById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getInvitationByIdMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetInvitationById<
  TData = Awaited<ReturnType<typeof getInvitationById>>,
  TError = Error,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInvitationById>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInvitationById>>,
          TError,
          Awaited<ReturnType<typeof getInvitationById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getInvitationByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetInvitationById<
  TData = Awaited<ReturnType<typeof getInvitationById>>,
  TError = Error,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInvitationById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getInvitationByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get Invitation by Id
 */

export function useGetInvitationById<
  TData = Awaited<ReturnType<typeof getInvitationById>>,
  TError = Error,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInvitationById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getInvitationByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetInvitationByIdQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update invitation
 */
export const updateInvitation = (
  id: number,
  updateInvitationRequestBody: UpdateInvitationRequestBody,
  options?: SecondParameter<typeof updateInvitationMutator>,
  signal?: AbortSignal
) => {
  return updateInvitationMutator<Invitation>(
    {
      url: `/v1/invitations/${id}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: updateInvitationRequestBody,
      signal,
    },
    options
  );
};

export const getUpdateInvitationMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateInvitation>>,
    TError,
    { id: number; data: UpdateInvitationRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof updateInvitationMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateInvitation>>,
  TError,
  { id: number; data: UpdateInvitationRequestBody },
  TContext
> => {
  const mutationKey = ['updateInvitation'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateInvitation>>,
    { id: number; data: UpdateInvitationRequestBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateInvitation(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateInvitationMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateInvitation>>
>;
export type UpdateInvitationMutationBody = UpdateInvitationRequestBody;
export type UpdateInvitationMutationError = Error;

/**
 * @summary Update invitation
 */
export const useUpdateInvitation = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateInvitation>>,
      TError,
      { id: number; data: UpdateInvitationRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof updateInvitationMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateInvitation>>,
  TError,
  { id: number; data: UpdateInvitationRequestBody },
  TContext
> => {
  return useMutation(getUpdateInvitationMutationOptions(options), queryClient);
};

/**
 * @summary Update invitation status
 */
export const updateInvitationStatus = (
  id: number,
  updateInvitationStatusRequestBody: UpdateInvitationStatusRequestBody,
  options?: SecondParameter<typeof updateInvitationStatusMutator>,
  signal?: AbortSignal
) => {
  return updateInvitationStatusMutator<void>(
    {
      url: `/v1/invitations/status/${id}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: updateInvitationStatusRequestBody,
      signal,
    },
    options
  );
};

export const getUpdateInvitationStatusMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateInvitationStatus>>,
    TError,
    { id: number; data: UpdateInvitationStatusRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof updateInvitationStatusMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateInvitationStatus>>,
  TError,
  { id: number; data: UpdateInvitationStatusRequestBody },
  TContext
> => {
  const mutationKey = ['updateInvitationStatus'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateInvitationStatus>>,
    { id: number; data: UpdateInvitationStatusRequestBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateInvitationStatus(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateInvitationStatusMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateInvitationStatus>>
>;
export type UpdateInvitationStatusMutationBody =
  UpdateInvitationStatusRequestBody;
export type UpdateInvitationStatusMutationError = Error;

/**
 * @summary Update invitation status
 */
export const useUpdateInvitationStatus = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateInvitationStatus>>,
      TError,
      { id: number; data: UpdateInvitationStatusRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof updateInvitationStatusMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateInvitationStatus>>,
  TError,
  { id: number; data: UpdateInvitationStatusRequestBody },
  TContext
> => {
  return useMutation(
    getUpdateInvitationStatusMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Cancel invitation (no further use)
 */
export const cancelInvitation = (
  id: number,
  options?: SecondParameter<typeof cancelInvitationMutator>,
  signal?: AbortSignal
) => {
  return cancelInvitationMutator<void>(
    { url: `/v1/invitations/${id}/cancel`, method: 'POST', signal },
    options
  );
};

export const getCancelInvitationMutationOptions = <
  TError = NotFoundResponse | Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelInvitation>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof cancelInvitationMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelInvitation>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ['cancelInvitation'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelInvitation>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return cancelInvitation(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelInvitationMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelInvitation>>
>;

export type CancelInvitationMutationError = NotFoundResponse | Error;

/**
 * @summary Cancel invitation (no further use)
 */
export const useCancelInvitation = <
  TError = NotFoundResponse | Error,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof cancelInvitation>>,
      TError,
      { id: number },
      TContext
    >;
    request?: SecondParameter<typeof cancelInvitationMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof cancelInvitation>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getCancelInvitationMutationOptions(options), queryClient);
};

/**
 * @summary Resend invitation email
 */
export const resendInvitation = (
  id: number,
  options?: SecondParameter<typeof resendInvitationMutator>,
  signal?: AbortSignal
) => {
  return resendInvitationMutator<void>(
    { url: `/v1/invitations/${id}/resend`, method: 'POST', signal },
    options
  );
};

export const getResendInvitationMutationOptions = <
  TError = NotFoundResponse | Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resendInvitation>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof resendInvitationMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resendInvitation>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ['resendInvitation'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resendInvitation>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return resendInvitation(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResendInvitationMutationResult = NonNullable<
  Awaited<ReturnType<typeof resendInvitation>>
>;

export type ResendInvitationMutationError = NotFoundResponse | Error;

/**
 * @summary Resend invitation email
 */
export const useResendInvitation = <
  TError = NotFoundResponse | Error,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resendInvitation>>,
      TError,
      { id: number },
      TContext
    >;
    request?: SecondParameter<typeof resendInvitationMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof resendInvitation>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getResendInvitationMutationOptions(options), queryClient);
};

/**
 * @summary Duplicate invitation
 */
export const duplicateInvitation = (
  id: number,
  duplicateInvitationRequestBody: DuplicateInvitationRequestBody,
  options?: SecondParameter<typeof duplicateInvitationMutator>,
  signal?: AbortSignal
) => {
  return duplicateInvitationMutator<Invitation>(
    {
      url: `/v1/invitations/duplicate/${id}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: duplicateInvitationRequestBody,
      signal,
    },
    options
  );
};

export const getDuplicateInvitationMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof duplicateInvitation>>,
    TError,
    { id: number; data: DuplicateInvitationRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof duplicateInvitationMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof duplicateInvitation>>,
  TError,
  { id: number; data: DuplicateInvitationRequestBody },
  TContext
> => {
  const mutationKey = ['duplicateInvitation'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof duplicateInvitation>>,
    { id: number; data: DuplicateInvitationRequestBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return duplicateInvitation(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DuplicateInvitationMutationResult = NonNullable<
  Awaited<ReturnType<typeof duplicateInvitation>>
>;
export type DuplicateInvitationMutationBody = DuplicateInvitationRequestBody;
export type DuplicateInvitationMutationError = Error;

/**
 * @summary Duplicate invitation
 */
export const useDuplicateInvitation = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof duplicateInvitation>>,
      TError,
      { id: number; data: DuplicateInvitationRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof duplicateInvitationMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof duplicateInvitation>>,
  TError,
  { id: number; data: DuplicateInvitationRequestBody },
  TContext
> => {
  return useMutation(
    getDuplicateInvitationMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Get one-time token for invitation
 */
export const getOneTimeToken = (
  invitationId: number,
  options?: SecondParameter<typeof getOneTimeTokenMutator>,
  signal?: AbortSignal
) => {
  return getOneTimeTokenMutator<string>(
    {
      url: `/v1/invitations/${invitationId}/one-time-token`,
      method: 'GET',
      signal,
    },
    options
  );
};

export const getGetOneTimeTokenQueryKey = (invitationId: number) => {
  return [`/v1/invitations/${invitationId}/one-time-token`] as const;
};

export const getGetOneTimeTokenQueryOptions = <
  TData = Awaited<ReturnType<typeof getOneTimeToken>>,
  TError = Error,
>(
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOneTimeToken>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getOneTimeTokenMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOneTimeTokenQueryKey(invitationId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOneTimeToken>>> = ({
    signal,
  }) => getOneTimeToken(invitationId, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!invitationId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOneTimeToken>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOneTimeTokenQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOneTimeToken>>
>;
export type GetOneTimeTokenQueryError = Error;

export function useGetOneTimeToken<
  TData = Awaited<ReturnType<typeof getOneTimeToken>>,
  TError = Error,
>(
  invitationId: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOneTimeToken>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOneTimeToken>>,
          TError,
          Awaited<ReturnType<typeof getOneTimeToken>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getOneTimeTokenMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetOneTimeToken<
  TData = Awaited<ReturnType<typeof getOneTimeToken>>,
  TError = Error,
>(
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOneTimeToken>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOneTimeToken>>,
          TError,
          Awaited<ReturnType<typeof getOneTimeToken>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getOneTimeTokenMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOneTimeToken<
  TData = Awaited<ReturnType<typeof getOneTimeToken>>,
  TError = Error,
>(
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOneTimeToken>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getOneTimeTokenMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get one-time token for invitation
 */

export function useGetOneTimeToken<
  TData = Awaited<ReturnType<typeof getOneTimeToken>>,
  TError = Error,
>(
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOneTimeToken>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getOneTimeTokenMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOneTimeTokenQueryOptions(invitationId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Guest magic link consumption (token -> session)
 */
export const verifyInvitedGuestToken = (
  params: VerifyInvitedGuestTokenParams,
  options?: SecondParameter<typeof verifyInvitedGuestTokenMutator>,
  signal?: AbortSignal
) => {
  return verifyInvitedGuestTokenMutator<void>(
    { url: `/v1/guests/invite`, method: 'GET', params, signal },
    options
  );
};

export const getVerifyInvitedGuestTokenQueryKey = (
  params?: VerifyInvitedGuestTokenParams
) => {
  return [`/v1/guests/invite`, ...(params ? [params] : [])] as const;
};

export const getVerifyInvitedGuestTokenQueryOptions = <
  TData = Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  params: VerifyInvitedGuestTokenParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof verifyInvitedGuestTokenMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getVerifyInvitedGuestTokenQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof verifyInvitedGuestToken>>
  > = ({ signal }) => verifyInvitedGuestToken(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type VerifyInvitedGuestTokenQueryResult = NonNullable<
  Awaited<ReturnType<typeof verifyInvitedGuestToken>>
>;
export type VerifyInvitedGuestTokenQueryError =
  | BadRequestResponse
  | NotFoundResponse;

export function useVerifyInvitedGuestToken<
  TData = Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  params: VerifyInvitedGuestTokenParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
          TError,
          Awaited<ReturnType<typeof verifyInvitedGuestToken>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof verifyInvitedGuestTokenMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useVerifyInvitedGuestToken<
  TData = Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  params: VerifyInvitedGuestTokenParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
          TError,
          Awaited<ReturnType<typeof verifyInvitedGuestToken>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof verifyInvitedGuestTokenMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useVerifyInvitedGuestToken<
  TData = Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  params: VerifyInvitedGuestTokenParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof verifyInvitedGuestTokenMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Guest magic link consumption (token -> session)
 */

export function useVerifyInvitedGuestToken<
  TData = Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  params: VerifyInvitedGuestTokenParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof verifyInvitedGuestToken>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof verifyInvitedGuestTokenMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getVerifyInvitedGuestTokenQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List all guests of an event
 */
export const listAllGuests = (
  listGuestsRequestBody: ListGuestsRequestBody,
  options?: SecondParameter<typeof listAllGuestsMutator>,
  signal?: AbortSignal
) => {
  return listAllGuestsMutator<ArrayOfGuest>(
    {
      url: `/v1/guests`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal,
    },
    options
  );
};

export const getListAllGuestsQueryKey = (
  listGuestsRequestBody?: ListGuestsRequestBody
) => {
  return [`/v1/guests`, listGuestsRequestBody] as const;
};

export const getListAllGuestsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAllGuests>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  listGuestsRequestBody: ListGuestsRequestBody,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAllGuests>>, TError, TData>
    >;
    request?: SecondParameter<typeof listAllGuestsMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListAllGuestsQueryKey(listGuestsRequestBody);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAllGuests>>> = ({
    signal,
  }) => listAllGuests(listGuestsRequestBody, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAllGuests>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListAllGuestsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAllGuests>>
>;
export type ListAllGuestsQueryError = BadRequestResponse | NotFoundResponse;

export function useListAllGuests<
  TData = Awaited<ReturnType<typeof listAllGuests>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  listGuestsRequestBody: ListGuestsRequestBody,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAllGuests>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAllGuests>>,
          TError,
          Awaited<ReturnType<typeof listAllGuests>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof listAllGuestsMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListAllGuests<
  TData = Awaited<ReturnType<typeof listAllGuests>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  listGuestsRequestBody: ListGuestsRequestBody,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAllGuests>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAllGuests>>,
          TError,
          Awaited<ReturnType<typeof listAllGuests>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof listAllGuestsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListAllGuests<
  TData = Awaited<ReturnType<typeof listAllGuests>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  listGuestsRequestBody: ListGuestsRequestBody,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAllGuests>>, TError, TData>
    >;
    request?: SecondParameter<typeof listAllGuestsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List all guests of an event
 */

export function useListAllGuests<
  TData = Awaited<ReturnType<typeof listAllGuests>>,
  TError = BadRequestResponse | NotFoundResponse,
>(
  listGuestsRequestBody: ListGuestsRequestBody,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAllGuests>>, TError, TData>
    >;
    request?: SecondParameter<typeof listAllGuestsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getListAllGuestsQueryOptions(
    listGuestsRequestBody,
    options
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get titles list for guest profile
 */
export const getTitles = (
  options?: SecondParameter<typeof getTitlesMutator>,
  signal?: AbortSignal
) => {
  return getTitlesMutator<string[]>(
    { url: `/v1/guest/titles`, method: 'GET', signal },
    options
  );
};

export const getGetTitlesQueryKey = () => {
  return [`/v1/guest/titles`] as const;
};

export const getGetTitlesQueryOptions = <
  TData = Awaited<ReturnType<typeof getTitles>>,
  TError = void,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getTitles>>, TError, TData>
  >;
  request?: SecondParameter<typeof getTitlesMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTitlesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTitles>>> = ({
    signal,
  }) => getTitles(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getTitles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTitlesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTitles>>
>;
export type GetTitlesQueryError = void;

export function useGetTitles<
  TData = Awaited<ReturnType<typeof getTitles>>,
  TError = void,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTitles>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTitles>>,
          TError,
          Awaited<ReturnType<typeof getTitles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getTitlesMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTitles<
  TData = Awaited<ReturnType<typeof getTitles>>,
  TError = void,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTitles>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTitles>>,
          TError,
          Awaited<ReturnType<typeof getTitles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getTitlesMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTitles<
  TData = Awaited<ReturnType<typeof getTitles>>,
  TError = void,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTitles>>, TError, TData>
    >;
    request?: SecondParameter<typeof getTitlesMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get titles list for guest profile
 */

export function useGetTitles<
  TData = Awaited<ReturnType<typeof getTitles>>,
  TError = void,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getTitles>>, TError, TData>
    >;
    request?: SecondParameter<typeof getTitlesMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetTitlesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get countries list for guest profile
 */
export const getCountries = (
  options?: SecondParameter<typeof getCountriesMutator>,
  signal?: AbortSignal
) => {
  return getCountriesMutator<Country[]>(
    { url: `/v1/guest/countries`, method: 'GET', signal },
    options
  );
};

export const getGetCountriesQueryKey = () => {
  return [`/v1/guest/countries`] as const;
};

export const getGetCountriesQueryOptions = <
  TData = Awaited<ReturnType<typeof getCountries>>,
  TError = void,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getCountries>>, TError, TData>
  >;
  request?: SecondParameter<typeof getCountriesMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCountriesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCountries>>> = ({
    signal,
  }) => getCountries(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCountries>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCountriesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCountries>>
>;
export type GetCountriesQueryError = void;

export function useGetCountries<
  TData = Awaited<ReturnType<typeof getCountries>>,
  TError = void,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountries>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCountries>>,
          TError,
          Awaited<ReturnType<typeof getCountries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getCountriesMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCountries<
  TData = Awaited<ReturnType<typeof getCountries>>,
  TError = void,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountries>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCountries>>,
          TError,
          Awaited<ReturnType<typeof getCountries>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getCountriesMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCountries<
  TData = Awaited<ReturnType<typeof getCountries>>,
  TError = void,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountries>>, TError, TData>
    >;
    request?: SecondParameter<typeof getCountriesMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get countries list for guest profile
 */

export function useGetCountries<
  TData = Awaited<ReturnType<typeof getCountries>>,
  TError = void,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountries>>, TError, TData>
    >;
    request?: SecondParameter<typeof getCountriesMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetCountriesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Check if gender neutral inbound support is enabled
 */
export const isEnableGenderNeutralInboundSupport = (
  params: IsEnableGenderNeutralInboundSupportParams,
  options?: SecondParameter<typeof isEnableGenderNeutralInboundSupportMutator>,
  signal?: AbortSignal
) => {
  return isEnableGenderNeutralInboundSupportMutator<boolean>(
    { url: `/v1/guest/allGendersSupported`, method: 'GET', params, signal },
    options
  );
};

export const getIsEnableGenderNeutralInboundSupportQueryKey = (
  params?: IsEnableGenderNeutralInboundSupportParams
) => {
  return [
    `/v1/guest/allGendersSupported`,
    ...(params ? [params] : []),
  ] as const;
};

export const getIsEnableGenderNeutralInboundSupportQueryOptions = <
  TData = Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
  TError = void,
>(
  params: IsEnableGenderNeutralInboundSupportParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<
      typeof isEnableGenderNeutralInboundSupportMutator
    >;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getIsEnableGenderNeutralInboundSupportQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>
  > = ({ signal }) =>
    isEnableGenderNeutralInboundSupport(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type IsEnableGenderNeutralInboundSupportQueryResult = NonNullable<
  Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>
>;
export type IsEnableGenderNeutralInboundSupportQueryError = void;

export function useIsEnableGenderNeutralInboundSupport<
  TData = Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
  TError = void,
>(
  params: IsEnableGenderNeutralInboundSupportParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
          TError,
          Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<
      typeof isEnableGenderNeutralInboundSupportMutator
    >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useIsEnableGenderNeutralInboundSupport<
  TData = Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
  TError = void,
>(
  params: IsEnableGenderNeutralInboundSupportParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
          TError,
          Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<
      typeof isEnableGenderNeutralInboundSupportMutator
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useIsEnableGenderNeutralInboundSupport<
  TData = Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
  TError = void,
>(
  params: IsEnableGenderNeutralInboundSupportParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<
      typeof isEnableGenderNeutralInboundSupportMutator
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Check if gender neutral inbound support is enabled
 */

export function useIsEnableGenderNeutralInboundSupport<
  TData = Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
  TError = void,
>(
  params: IsEnableGenderNeutralInboundSupportParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof isEnableGenderNeutralInboundSupport>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<
      typeof isEnableGenderNeutralInboundSupportMutator
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getIsEnableGenderNeutralInboundSupportQueryOptions(
    params,
    options
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get country code
 */
export const getCountryCode = (
  params: GetCountryCodeParams,
  options?: SecondParameter<typeof getCountryCodeMutator>,
  signal?: AbortSignal
) => {
  return getCountryCodeMutator<string>(
    { url: `/v1/countryCode`, method: 'GET', params, signal },
    options
  );
};

export const getGetCountryCodeQueryKey = (params?: GetCountryCodeParams) => {
  return [`/v1/countryCode`, ...(params ? [params] : [])] as const;
};

export const getGetCountryCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof getCountryCode>>,
  TError = void,
>(
  params: GetCountryCodeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountryCode>>, TError, TData>
    >;
    request?: SecondParameter<typeof getCountryCodeMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCountryCodeQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCountryCode>>> = ({
    signal,
  }) => getCountryCode(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCountryCode>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCountryCodeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCountryCode>>
>;
export type GetCountryCodeQueryError = void;

export function useGetCountryCode<
  TData = Awaited<ReturnType<typeof getCountryCode>>,
  TError = void,
>(
  params: GetCountryCodeParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountryCode>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCountryCode>>,
          TError,
          Awaited<ReturnType<typeof getCountryCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getCountryCodeMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCountryCode<
  TData = Awaited<ReturnType<typeof getCountryCode>>,
  TError = void,
>(
  params: GetCountryCodeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountryCode>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCountryCode>>,
          TError,
          Awaited<ReturnType<typeof getCountryCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getCountryCodeMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCountryCode<
  TData = Awaited<ReturnType<typeof getCountryCode>>,
  TError = void,
>(
  params: GetCountryCodeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountryCode>>, TError, TData>
    >;
    request?: SecondParameter<typeof getCountryCodeMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get country code
 */

export function useGetCountryCode<
  TData = Awaited<ReturnType<typeof getCountryCode>>,
  TError = void,
>(
  params: GetCountryCodeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCountryCode>>, TError, TData>
    >;
    request?: SecondParameter<typeof getCountryCodeMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetCountryCodeQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get guest by ID
 */
export const getGuestById = (
  id: number,
  options?: SecondParameter<typeof getGuestByIdMutator>,
  signal?: AbortSignal
) => {
  return getGuestByIdMutator<Guest>(
    { url: `/v1/guest/${id}`, method: 'GET', signal },
    options
  );
};

export const getGetGuestByIdQueryKey = (id: number) => {
  return [`/v1/guest/${id}`] as const;
};

export const getGetGuestByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getGuestById>>,
  TError = void,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getGuestById>>, TError, TData>
    >;
    request?: SecondParameter<typeof getGuestByIdMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGuestByIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getGuestById>>> = ({
    signal,
  }) => getGuestById(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getGuestById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetGuestByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGuestById>>
>;
export type GetGuestByIdQueryError = void;

export function useGetGuestById<
  TData = Awaited<ReturnType<typeof getGuestById>>,
  TError = void,
>(
  id: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getGuestById>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGuestById>>,
          TError,
          Awaited<ReturnType<typeof getGuestById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getGuestByIdMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetGuestById<
  TData = Awaited<ReturnType<typeof getGuestById>>,
  TError = void,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getGuestById>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGuestById>>,
          TError,
          Awaited<ReturnType<typeof getGuestById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getGuestByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetGuestById<
  TData = Awaited<ReturnType<typeof getGuestById>>,
  TError = void,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getGuestById>>, TError, TData>
    >;
    request?: SecondParameter<typeof getGuestByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get guest by ID
 */

export function useGetGuestById<
  TData = Awaited<ReturnType<typeof getGuestById>>,
  TError = void,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getGuestById>>, TError, TData>
    >;
    request?: SecondParameter<typeof getGuestByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetGuestByIdQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update guest profile in eventmgt service
 */
export const updateGuestProfile = (
  id: number,
  updateProfileRequestBody: UpdateProfileRequestBody,
  options?: SecondParameter<typeof updateGuestProfileMutator>,
  signal?: AbortSignal
) => {
  return updateGuestProfileMutator<Guest>(
    {
      url: `/v1/guests/profile/${id}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: updateProfileRequestBody,
      signal,
    },
    options
  );
};

export const getUpdateGuestProfileMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateGuestProfile>>,
    TError,
    { id: number; data: UpdateProfileRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof updateGuestProfileMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateGuestProfile>>,
  TError,
  { id: number; data: UpdateProfileRequestBody },
  TContext
> => {
  const mutationKey = ['updateGuestProfile'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateGuestProfile>>,
    { id: number; data: UpdateProfileRequestBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateGuestProfile(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateGuestProfileMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateGuestProfile>>
>;
export type UpdateGuestProfileMutationBody = UpdateProfileRequestBody;
export type UpdateGuestProfileMutationError = Error;

/**
 * @summary Update guest profile in eventmgt service
 */
export const useUpdateGuestProfile = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateGuestProfile>>,
      TError,
      { id: number; data: UpdateProfileRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof updateGuestProfileMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateGuestProfile>>,
  TError,
  { id: number; data: UpdateProfileRequestBody },
  TContext
> => {
  return useMutation(
    getUpdateGuestProfileMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Create/attach guest profile in Zeno
 */
export const createGuestProfile = (
  createGuestRequestBody: CreateGuestRequestBody,
  options?: SecondParameter<typeof createGuestProfileMutator>,
  signal?: AbortSignal
) => {
  return createGuestProfileMutator<Guest>(
    {
      url: `/v1/guest/profile`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createGuestRequestBody,
      signal,
    },
    options
  );
};

export const getCreateGuestProfileMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createGuestProfile>>,
    TError,
    { data: CreateGuestRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof createGuestProfileMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createGuestProfile>>,
  TError,
  { data: CreateGuestRequestBody },
  TContext
> => {
  const mutationKey = ['createGuestProfile'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createGuestProfile>>,
    { data: CreateGuestRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return createGuestProfile(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateGuestProfileMutationResult = NonNullable<
  Awaited<ReturnType<typeof createGuestProfile>>
>;
export type CreateGuestProfileMutationBody = CreateGuestRequestBody;
export type CreateGuestProfileMutationError = Error;

/**
 * @summary Create/attach guest profile in Zeno
 */
export const useCreateGuestProfile = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createGuestProfile>>,
      TError,
      { data: CreateGuestRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof createGuestProfileMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createGuestProfile>>,
  TError,
  { data: CreateGuestRequestBody },
  TContext
> => {
  return useMutation(
    getCreateGuestProfileMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Get invitation by guest
 */
export const getGuestInvitationById = (
  id: number,
  options?: SecondParameter<typeof getGuestInvitationByIdMutator>,
  signal?: AbortSignal
) => {
  return getGuestInvitationByIdMutator<Invitation>(
    { url: `/v1/guest/invitation/${id}`, method: 'GET', signal },
    options
  );
};

export const getGetGuestInvitationByIdQueryKey = (id: number) => {
  return [`/v1/guest/invitation/${id}`] as const;
};

export const getGetGuestInvitationByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getGuestInvitationById>>,
  TError = Error,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGuestInvitationById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getGuestInvitationByIdMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetGuestInvitationByIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getGuestInvitationById>>
  > = ({ signal }) => getGuestInvitationById(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getGuestInvitationById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetGuestInvitationByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGuestInvitationById>>
>;
export type GetGuestInvitationByIdQueryError = Error;

export function useGetGuestInvitationById<
  TData = Awaited<ReturnType<typeof getGuestInvitationById>>,
  TError = Error,
>(
  id: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGuestInvitationById>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGuestInvitationById>>,
          TError,
          Awaited<ReturnType<typeof getGuestInvitationById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getGuestInvitationByIdMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetGuestInvitationById<
  TData = Awaited<ReturnType<typeof getGuestInvitationById>>,
  TError = Error,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGuestInvitationById>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGuestInvitationById>>,
          TError,
          Awaited<ReturnType<typeof getGuestInvitationById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getGuestInvitationByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetGuestInvitationById<
  TData = Awaited<ReturnType<typeof getGuestInvitationById>>,
  TError = Error,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGuestInvitationById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getGuestInvitationByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get invitation by guest
 */

export function useGetGuestInvitationById<
  TData = Awaited<ReturnType<typeof getGuestInvitationById>>,
  TError = Error,
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGuestInvitationById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getGuestInvitationByIdMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetGuestInvitationByIdQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update invitation by guest
 */
export const updateGuestInvitation = (
  id: number,
  updateInvitationRequestBody: UpdateInvitationRequestBody,
  options?: SecondParameter<typeof updateGuestInvitationMutator>,
  signal?: AbortSignal
) => {
  return updateGuestInvitationMutator<Invitation>(
    {
      url: `/v1/guest/invitation/${id}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: updateInvitationRequestBody,
      signal,
    },
    options
  );
};

export const getUpdateGuestInvitationMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateGuestInvitation>>,
    TError,
    { id: number; data: UpdateInvitationRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof updateGuestInvitationMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateGuestInvitation>>,
  TError,
  { id: number; data: UpdateInvitationRequestBody },
  TContext
> => {
  const mutationKey = ['updateGuestInvitation'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateGuestInvitation>>,
    { id: number; data: UpdateInvitationRequestBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateGuestInvitation(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateGuestInvitationMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateGuestInvitation>>
>;
export type UpdateGuestInvitationMutationBody = UpdateInvitationRequestBody;
export type UpdateGuestInvitationMutationError = Error;

/**
 * @summary Update invitation by guest
 */
export const useUpdateGuestInvitation = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateGuestInvitation>>,
      TError,
      { id: number; data: UpdateInvitationRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof updateGuestInvitationMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateGuestInvitation>>,
  TError,
  { id: number; data: UpdateInvitationRequestBody },
  TContext
> => {
  return useMutation(
    getUpdateGuestInvitationMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Confirm booking for guest invitation
 */
export const confirmGuestBooking = (
  id: number,
  bookingConfirmedRequestBody: BookingConfirmedRequestBody,
  options?: SecondParameter<typeof confirmGuestBookingMutator>,
  signal?: AbortSignal
) => {
  return confirmGuestBookingMutator<void>(
    {
      url: `/v1/guest/booking/${id}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: bookingConfirmedRequestBody,
      signal,
    },
    options
  );
};

export const getConfirmGuestBookingMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof confirmGuestBooking>>,
    TError,
    { id: number; data: BookingConfirmedRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof confirmGuestBookingMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof confirmGuestBooking>>,
  TError,
  { id: number; data: BookingConfirmedRequestBody },
  TContext
> => {
  const mutationKey = ['confirmGuestBooking'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof confirmGuestBooking>>,
    { id: number; data: BookingConfirmedRequestBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return confirmGuestBooking(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ConfirmGuestBookingMutationResult = NonNullable<
  Awaited<ReturnType<typeof confirmGuestBooking>>
>;
export type ConfirmGuestBookingMutationBody = BookingConfirmedRequestBody;
export type ConfirmGuestBookingMutationError = Error;

/**
 * @summary Confirm booking for guest invitation
 */
export const useConfirmGuestBooking = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof confirmGuestBooking>>,
      TError,
      { id: number; data: BookingConfirmedRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof confirmGuestBookingMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof confirmGuestBooking>>,
  TError,
  { id: number; data: BookingConfirmedRequestBody },
  TContext
> => {
  return useMutation(
    getConfirmGuestBookingMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Get verify code for guest invitation
 */
export const getVerifyCode = (
  invitationId: number,
  options?: SecondParameter<typeof getVerifyCodeMutator>,
  signal?: AbortSignal
) => {
  return getVerifyCodeMutator<string>(
    {
      url: `/v1/guest/invitation/${invitationId}/verify-code`,
      method: 'GET',
      signal,
    },
    options
  );
};

export const getGetVerifyCodeQueryKey = (invitationId: number) => {
  return [`/v1/guest/invitation/${invitationId}/verify-code`] as const;
};

export const getGetVerifyCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof getVerifyCode>>,
  TError = Error,
>(
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getVerifyCode>>, TError, TData>
    >;
    request?: SecondParameter<typeof getVerifyCodeMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetVerifyCodeQueryKey(invitationId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getVerifyCode>>> = ({
    signal,
  }) => getVerifyCode(invitationId, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!invitationId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getVerifyCode>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetVerifyCodeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVerifyCode>>
>;
export type GetVerifyCodeQueryError = Error;

export function useGetVerifyCode<
  TData = Awaited<ReturnType<typeof getVerifyCode>>,
  TError = Error,
>(
  invitationId: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getVerifyCode>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVerifyCode>>,
          TError,
          Awaited<ReturnType<typeof getVerifyCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getVerifyCodeMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetVerifyCode<
  TData = Awaited<ReturnType<typeof getVerifyCode>>,
  TError = Error,
>(
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getVerifyCode>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVerifyCode>>,
          TError,
          Awaited<ReturnType<typeof getVerifyCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getVerifyCodeMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetVerifyCode<
  TData = Awaited<ReturnType<typeof getVerifyCode>>,
  TError = Error,
>(
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getVerifyCode>>, TError, TData>
    >;
    request?: SecondParameter<typeof getVerifyCodeMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get verify code for guest invitation
 */

export function useGetVerifyCode<
  TData = Awaited<ReturnType<typeof getVerifyCode>>,
  TError = Error,
>(
  invitationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getVerifyCode>>, TError, TData>
    >;
    request?: SecondParameter<typeof getVerifyCodeMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetVerifyCodeQueryOptions(invitationId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create IBI payload for guest booking redirect to Zeno
 */
export const createGuestIbiPayload = (
  id: number,
  options?: SecondParameter<typeof createGuestIbiPayloadMutator>,
  signal?: AbortSignal
) => {
  return createGuestIbiPayloadMutator<CreateIbiPayloadResponse>(
    { url: `/v1/guest/ibi/${id}`, method: 'POST', signal },
    options
  );
};

export const getCreateGuestIbiPayloadMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createGuestIbiPayload>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof createGuestIbiPayloadMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createGuestIbiPayload>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ['createGuestIbiPayload'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createGuestIbiPayload>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return createGuestIbiPayload(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateGuestIbiPayloadMutationResult = NonNullable<
  Awaited<ReturnType<typeof createGuestIbiPayload>>
>;

export type CreateGuestIbiPayloadMutationError = Error;

/**
 * @summary Create IBI payload for guest booking redirect to Zeno
 */
export const useCreateGuestIbiPayload = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createGuestIbiPayload>>,
      TError,
      { id: number },
      TContext
    >;
    request?: SecondParameter<typeof createGuestIbiPayloadMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createGuestIbiPayload>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(
    getCreateGuestIbiPayloadMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Look up a traveller profile in Zeno by email address
 */
export const lookupTravellerByEmail = (
  params: LookupTravellerByEmailParams,
  options?: SecondParameter<typeof lookupTravellerByEmailMutator>,
  signal?: AbortSignal
) => {
  return lookupTravellerByEmailMutator<TravellerProfileLookup | void>(
    { url: `/v1/guests/traveller-profile`, method: 'GET', params, signal },
    options
  );
};

export const getLookupTravellerByEmailQueryKey = (
  params?: LookupTravellerByEmailParams
) => {
  return [`/v1/guests/traveller-profile`, ...(params ? [params] : [])] as const;
};

export const getLookupTravellerByEmailQueryOptions = <
  TData = Awaited<ReturnType<typeof lookupTravellerByEmail>>,
  TError = Error,
>(
  params: LookupTravellerByEmailParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof lookupTravellerByEmail>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof lookupTravellerByEmailMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getLookupTravellerByEmailQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof lookupTravellerByEmail>>
  > = ({ signal }) => lookupTravellerByEmail(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof lookupTravellerByEmail>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type LookupTravellerByEmailQueryResult = NonNullable<
  Awaited<ReturnType<typeof lookupTravellerByEmail>>
>;
export type LookupTravellerByEmailQueryError = Error;

export function useLookupTravellerByEmail<
  TData = Awaited<ReturnType<typeof lookupTravellerByEmail>>,
  TError = Error,
>(
  params: LookupTravellerByEmailParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof lookupTravellerByEmail>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof lookupTravellerByEmail>>,
          TError,
          Awaited<ReturnType<typeof lookupTravellerByEmail>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof lookupTravellerByEmailMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useLookupTravellerByEmail<
  TData = Awaited<ReturnType<typeof lookupTravellerByEmail>>,
  TError = Error,
>(
  params: LookupTravellerByEmailParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof lookupTravellerByEmail>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof lookupTravellerByEmail>>,
          TError,
          Awaited<ReturnType<typeof lookupTravellerByEmail>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof lookupTravellerByEmailMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useLookupTravellerByEmail<
  TData = Awaited<ReturnType<typeof lookupTravellerByEmail>>,
  TError = Error,
>(
  params: LookupTravellerByEmailParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof lookupTravellerByEmail>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof lookupTravellerByEmailMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Look up a traveller profile in Zeno by email address
 */

export function useLookupTravellerByEmail<
  TData = Awaited<ReturnType<typeof lookupTravellerByEmail>>,
  TError = Error,
>(
  params: LookupTravellerByEmailParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof lookupTravellerByEmail>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof lookupTravellerByEmailMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getLookupTravellerByEmailQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Search guests by bookerID and debtorID
 */
export const searchGuests = (
  params?: SearchGuestsParams,
  options?: SecondParameter<typeof searchGuestsMutator>,
  signal?: AbortSignal
) => {
  return searchGuestsMutator<ArrayOfGuest>(
    { url: `/v1/guests/SearchGuests`, method: 'GET', params, signal },
    options
  );
};

export const getSearchGuestsQueryKey = (params?: SearchGuestsParams) => {
  return [`/v1/guests/SearchGuests`, ...(params ? [params] : [])] as const;
};

export const getSearchGuestsQueryOptions = <
  TData = Awaited<ReturnType<typeof searchGuests>>,
  TError = Error,
>(
  params?: SearchGuestsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchGuests>>, TError, TData>
    >;
    request?: SecondParameter<typeof searchGuestsMutator>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchGuestsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchGuests>>> = ({
    signal,
  }) => searchGuests(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchGuests>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type SearchGuestsQueryResult = NonNullable<
  Awaited<ReturnType<typeof searchGuests>>
>;
export type SearchGuestsQueryError = Error;

export function useSearchGuests<
  TData = Awaited<ReturnType<typeof searchGuests>>,
  TError = Error,
>(
  params: undefined | SearchGuestsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchGuests>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchGuests>>,
          TError,
          Awaited<ReturnType<typeof searchGuests>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof searchGuestsMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useSearchGuests<
  TData = Awaited<ReturnType<typeof searchGuests>>,
  TError = Error,
>(
  params?: SearchGuestsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchGuests>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchGuests>>,
          TError,
          Awaited<ReturnType<typeof searchGuests>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof searchGuestsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useSearchGuests<
  TData = Awaited<ReturnType<typeof searchGuests>>,
  TError = Error,
>(
  params?: SearchGuestsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchGuests>>, TError, TData>
    >;
    request?: SecondParameter<typeof searchGuestsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Search guests by bookerID and debtorID
 */

export function useSearchGuests<
  TData = Awaited<ReturnType<typeof searchGuests>>,
  TError = Error,
>(
  params?: SearchGuestsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchGuests>>, TError, TData>
    >;
    request?: SecondParameter<typeof searchGuestsMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getSearchGuestsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the current minimum logging level for the application
 * @summary Get current minimum logging level
 */
export const getLoggingLevel = (
  options?: SecondParameter<typeof getLoggingLevelMutator>,
  signal?: AbortSignal
) => {
  return getLoggingLevelMutator<LoggingLevelResponse>(
    { url: `/v1/logging/level`, method: 'GET', signal },
    options
  );
};

export const getGetLoggingLevelQueryKey = () => {
  return [`/v1/logging/level`] as const;
};

export const getGetLoggingLevelQueryOptions = <
  TData = Awaited<ReturnType<typeof getLoggingLevel>>,
  TError = UnauthorizedResponse | Error,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getLoggingLevel>>, TError, TData>
  >;
  request?: SecondParameter<typeof getLoggingLevelMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLoggingLevelQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLoggingLevel>>> = ({
    signal,
  }) => getLoggingLevel(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLoggingLevel>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetLoggingLevelQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLoggingLevel>>
>;
export type GetLoggingLevelQueryError = UnauthorizedResponse | Error;

export function useGetLoggingLevel<
  TData = Awaited<ReturnType<typeof getLoggingLevel>>,
  TError = UnauthorizedResponse | Error,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLoggingLevel>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLoggingLevel>>,
          TError,
          Awaited<ReturnType<typeof getLoggingLevel>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getLoggingLevelMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetLoggingLevel<
  TData = Awaited<ReturnType<typeof getLoggingLevel>>,
  TError = UnauthorizedResponse | Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLoggingLevel>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLoggingLevel>>,
          TError,
          Awaited<ReturnType<typeof getLoggingLevel>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getLoggingLevelMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetLoggingLevel<
  TData = Awaited<ReturnType<typeof getLoggingLevel>>,
  TError = UnauthorizedResponse | Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLoggingLevel>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getLoggingLevelMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get current minimum logging level
 */

export function useGetLoggingLevel<
  TData = Awaited<ReturnType<typeof getLoggingLevel>>,
  TError = UnauthorizedResponse | Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLoggingLevel>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof getLoggingLevelMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetLoggingLevelQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Changes the minimum logging level at runtime without restarting the application. This is useful for troubleshooting production issues by temporarily increasing log verbosity.
 * @summary Set minimum logging level dynamically
 */
export const setLoggingLevel = (
  setLogLevelRequest: SetLogLevelRequest,
  options?: SecondParameter<typeof setLoggingLevelMutator>,
  signal?: AbortSignal
) => {
  return setLoggingLevelMutator<SetLogLevelResponse>(
    {
      url: `/v1/logging/level`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: setLogLevelRequest,
      signal,
    },
    options
  );
};

export const getSetLoggingLevelMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setLoggingLevel>>,
    TError,
    { data: SetLogLevelRequest },
    TContext
  >;
  request?: SecondParameter<typeof setLoggingLevelMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setLoggingLevel>>,
  TError,
  { data: SetLogLevelRequest },
  TContext
> => {
  const mutationKey = ['setLoggingLevel'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setLoggingLevel>>,
    { data: SetLogLevelRequest }
  > = (props) => {
    const { data } = props ?? {};

    return setLoggingLevel(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetLoggingLevelMutationResult = NonNullable<
  Awaited<ReturnType<typeof setLoggingLevel>>
>;
export type SetLoggingLevelMutationBody = SetLogLevelRequest;
export type SetLoggingLevelMutationError = Error;

/**
 * @summary Set minimum logging level dynamically
 */
export const useSetLoggingLevel = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setLoggingLevel>>,
      TError,
      { data: SetLogLevelRequest },
      TContext
    >;
    request?: SecondParameter<typeof setLoggingLevelMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof setLoggingLevel>>,
  TError,
  { data: SetLogLevelRequest },
  TContext
> => {
  return useMutation(getSetLoggingLevelMutationOptions(options), queryClient);
};

/**
 * @summary Guest authentication by token
 */
export const guestAuthByToken = (
  guestAuthRequestBody: GuestAuthRequestBody,
  options?: SecondParameter<typeof guestAuthByTokenMutator>,
  signal?: AbortSignal
) => {
  return guestAuthByTokenMutator<GuestAuthResponse>(
    {
      url: `/v1/guestauth/token`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: guestAuthRequestBody,
      signal,
    },
    options
  );
};

export const getGuestAuthByTokenMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof guestAuthByToken>>,
    TError,
    { data: GuestAuthRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof guestAuthByTokenMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof guestAuthByToken>>,
  TError,
  { data: GuestAuthRequestBody },
  TContext
> => {
  const mutationKey = ['guestAuthByToken'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof guestAuthByToken>>,
    { data: GuestAuthRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return guestAuthByToken(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GuestAuthByTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof guestAuthByToken>>
>;
export type GuestAuthByTokenMutationBody = GuestAuthRequestBody;
export type GuestAuthByTokenMutationError = void;

/**
 * @summary Guest authentication by token
 */
export const useGuestAuthByToken = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof guestAuthByToken>>,
      TError,
      { data: GuestAuthRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof guestAuthByTokenMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof guestAuthByToken>>,
  TError,
  { data: GuestAuthRequestBody },
  TContext
> => {
  return useMutation(getGuestAuthByTokenMutationOptions(options), queryClient);
};

/**
 * @summary Resend guest authentication code
 */
export const resendGuestAuthCode = (
  resendCodeRequestBody: ResendCodeRequestBody,
  options?: SecondParameter<typeof resendGuestAuthCodeMutator>,
  signal?: AbortSignal
) => {
  return resendGuestAuthCodeMutator<GuestAuthResponse>(
    {
      url: `/v1/guestauth/resendcode`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: resendCodeRequestBody,
      signal,
    },
    options
  );
};

export const getResendGuestAuthCodeMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resendGuestAuthCode>>,
    TError,
    { data: ResendCodeRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof resendGuestAuthCodeMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resendGuestAuthCode>>,
  TError,
  { data: ResendCodeRequestBody },
  TContext
> => {
  const mutationKey = ['resendGuestAuthCode'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resendGuestAuthCode>>,
    { data: ResendCodeRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return resendGuestAuthCode(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResendGuestAuthCodeMutationResult = NonNullable<
  Awaited<ReturnType<typeof resendGuestAuthCode>>
>;
export type ResendGuestAuthCodeMutationBody = ResendCodeRequestBody;
export type ResendGuestAuthCodeMutationError = void;

/**
 * @summary Resend guest authentication code
 */
export const useResendGuestAuthCode = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resendGuestAuthCode>>,
      TError,
      { data: ResendCodeRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof resendGuestAuthCodeMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof resendGuestAuthCode>>,
  TError,
  { data: ResendCodeRequestBody },
  TContext
> => {
  return useMutation(
    getResendGuestAuthCodeMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Verify guest authentication code
 */
export const verifyGuestAuthCode = (
  verifyCodeRequestBody: VerifyCodeRequestBody,
  options?: SecondParameter<typeof verifyGuestAuthCodeMutator>,
  signal?: AbortSignal
) => {
  return verifyGuestAuthCodeMutator<GuestAuthResponse>(
    {
      url: `/v1/guestauth/code`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: verifyCodeRequestBody,
      signal,
    },
    options
  );
};

export const getVerifyGuestAuthCodeMutationOptions = <
  TError = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof verifyGuestAuthCode>>,
    TError,
    { data: VerifyCodeRequestBody },
    TContext
  >;
  request?: SecondParameter<typeof verifyGuestAuthCodeMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof verifyGuestAuthCode>>,
  TError,
  { data: VerifyCodeRequestBody },
  TContext
> => {
  const mutationKey = ['verifyGuestAuthCode'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof verifyGuestAuthCode>>,
    { data: VerifyCodeRequestBody }
  > = (props) => {
    const { data } = props ?? {};

    return verifyGuestAuthCode(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyGuestAuthCodeMutationResult = NonNullable<
  Awaited<ReturnType<typeof verifyGuestAuthCode>>
>;
export type VerifyGuestAuthCodeMutationBody = VerifyCodeRequestBody;
export type VerifyGuestAuthCodeMutationError = void;

/**
 * @summary Verify guest authentication code
 */
export const useVerifyGuestAuthCode = <TError = void, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof verifyGuestAuthCode>>,
      TError,
      { data: VerifyCodeRequestBody },
      TContext
    >;
    request?: SecondParameter<typeof verifyGuestAuthCodeMutator>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof verifyGuestAuthCode>>,
  TError,
  { data: VerifyCodeRequestBody },
  TContext
> => {
  return useMutation(
    getVerifyGuestAuthCodeMutationOptions(options),
    queryClient
  );
};

/**
 * @summary Get current user profile
 */
export const getUserProfile = (
  options?: SecondParameter<typeof getUserProfileMutator>,
  signal?: AbortSignal
) => {
  return getUserProfileMutator<CurrentUserProfile>(
    { url: `/v1/user/profile`, method: 'GET', signal },
    options
  );
};

export const getGetUserProfileQueryKey = () => {
  return [`/v1/user/profile`] as const;
};

export const getGetUserProfileQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserProfile>>,
  TError = Error,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getUserProfile>>, TError, TData>
  >;
  request?: SecondParameter<typeof getUserProfileMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserProfileQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserProfile>>> = ({
    signal,
  }) => getUserProfile(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserProfile>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUserProfileQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserProfile>>
>;
export type GetUserProfileQueryError = Error;

export function useGetUserProfile<
  TData = Awaited<ReturnType<typeof getUserProfile>>,
  TError = Error,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProfile>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserProfile>>,
          TError,
          Awaited<ReturnType<typeof getUserProfile>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getUserProfileMutator>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetUserProfile<
  TData = Awaited<ReturnType<typeof getUserProfile>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProfile>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserProfile>>,
          TError,
          Awaited<ReturnType<typeof getUserProfile>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof getUserProfileMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserProfile<
  TData = Awaited<ReturnType<typeof getUserProfile>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProfile>>, TError, TData>
    >;
    request?: SecondParameter<typeof getUserProfileMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get current user profile
 */

export function useGetUserProfile<
  TData = Awaited<ReturnType<typeof getUserProfile>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProfile>>, TError, TData>
    >;
    request?: SecondParameter<typeof getUserProfileMutator>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetUserProfileQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  return { ...query, queryKey: queryOptions.queryKey };
}
